import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import RejectConfirmationDialog from "@/pages/refund/components/RejectConfirmationDialog";
import { FlightCancellationReason, FlightRefundStatus } from "@/types";
import { tooltipStyles } from "@/utils/tooltipStyles";
import { truncateAddress } from "@/utils/truncateText";
import {
  getFlightCancellationReasonLabel,
  getNetworkDisplayName,
} from "@/utils/utils";
import { Dialog, Theme, Tooltip, useMediaQuery } from "@mui/material";
import { useState } from "react";
import toast from "react-hot-toast";

interface ReviewRefundRequestModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (refundAmount: number) => void;
  onReject: () => void;
  bookingCode: string;
  cancellationReason: FlightCancellationReason;
  orderContactEmail: string;
  totalRefundable?: number;
  walletAddress?: string | null;
  refundNetwork?: string | null;
  bankName?: string | null;
  bankAccountName?: string | null;
  bankAccountNumber?: string | null;
  status: FlightRefundStatus;
  reasonDetail: string;
}

const ReviewRefundFlightRequestModal = ({
  open,
  onClose,
  onConfirm,
  onReject,
  bookingCode,
  cancellationReason,
  orderContactEmail,
  totalRefundable,
  walletAddress,
  refundNetwork,
  bankName,
  bankAccountName,
  bankAccountNumber,
  status,
  reasonDetail,
}: ReviewRefundRequestModalProps) => {
  const [showRejectConfirmation, setShowRejectConfirmation] = useState(false);
  const [showMainModal, setShowMainModal] = useState(true);
  const [refundAmount, setRefundAmount] = useState<string>(
    `${totalRefundable ?? 0}`,
  );
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );
  const handleReject = () => {
    setShowRejectConfirmation(true);
    setShowMainModal(false);
  };

  const handleRejectConfirm = () => {
    onReject();
    setShowRejectConfirmation(false);
    setShowMainModal(false);
    onClose();
  };

  const handleRejectCancel = () => {
    setShowRejectConfirmation(false);
    setShowMainModal(true);
  };

  const handleClose = () => {
    setShowRejectConfirmation(false);
    setShowMainModal(false);
    onClose();
  };

  const [, copy] = useCopyToClipboard();

  const handleCopy = (text: string) => () => {
    copy(text)
      .then(() => {
        toast.success("Copied!");
      })
      .catch(() => {
        toast.error("Failed to copy!");
      });
  };

  const handleConfirm = () => {
    onConfirm(Number(refundAmount) || 0);
  };

  return (
    <>
      <RejectConfirmationDialog
        open={showRejectConfirmation}
        onClose={handleRejectCancel}
        onConfirm={handleRejectConfirm}
      />
      <Dialog
        open={open && showMainModal}
        onClose={handleClose}
        maxWidth={"md"}
        fullWidth
        disableScrollLock
        PaperProps={{
          sx: {
            borderRadius: "24px",
            width: `${isMobile ? "100%" : "670px"}`,
            margin: "16px",
          },
        }}
      >
        <div className="w-full rounded-[24px] px-8 py-6 max-sm:px-5">
          <div className="flex items-center justify-between border-b border-[#E2E8F0] pb-4">
            <h2 className="text-xl font-medium text-[#1E293B]">
              Review Refund Request
            </h2>
            <button onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="#334155"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
          <div className="mt-8">
            <p className="text-[16px] text-text max-sm:text-[14px]">
              Please review the following refund details before proceeding with
              approval or rejection.
            </p>
            <div className="mt-8 space-y-[10px]">
              <div className="flex">
                <span className="w-[175px] text-sm text-[#64748B]">
                  Booking ID
                </span>
                <span className="text-sm font-semibold text-[#1E293B]">
                  {bookingCode}
                </span>
              </div>
              <div className="flex">
                <span className="w-[175px] text-sm text-[#64748B]">
                  Reason of cancellation
                </span>
                <span className="text-sm font-semibold text-[#1E293B] max-sm:flex-1">
                  {getFlightCancellationReasonLabel(cancellationReason)}
                </span>
              </div>
              <div className="flex">
                <span className="w-[175px] shrink-0 text-sm text-[#64748B]">
                  Reason Detail
                </span>
                <span className="line-clamp-5 text-sm font-semibold text-[#1E293B] max-sm:flex-1">
                  {reasonDetail}
                </span>
              </div>
              <div className="flex">
                <span className="w-[175px] text-sm text-[#64748B]">
                  Email Contact
                </span>
                <span className="text-sm font-semibold text-[#1E293B]">
                  {orderContactEmail}
                </span>
              </div>
              <div className="flex items-center">
                <span className="w-[175px] text-sm text-[#64748B]">
                  Refund Amount
                </span>
                {status === FlightRefundStatus.PENDING_ADMIN ? (
                  <div className="flex h-12 w-1/2 items-center gap-3 rounded-lg border border-[#E2E8F0] bg-white px-4">
                    <span className="text-sm font-semibold text-[#1E293B]">
                      USD
                    </span>
                    <input
                      placeholder="Amount"
                      onChange={(e) => setRefundAmount(e.target.value)}
                      inputMode="search"
                      value={refundAmount}
                      className="w-full bg-white text-sm font-semibold text-[#1E293B] outline-none placeholder:text-[#94A3B8]"
                      type="number"
                    />
                  </div>
                ) : (
                  <span className="text-sm font-semibold text-[#1E293B]">
                    USD {totalRefundable}
                  </span>
                )}
              </div>
              {refundNetwork ? (
                <>
                  <div className="flex">
                    <span className="w-[175px] text-sm text-[#64748B]">
                      Network
                    </span>
                    <span className="text-sm font-semibold text-[#1E293B]">
                      {(() => {
                        if (refundNetwork && refundNetwork?.length > 15) {
                          return (
                            <Tooltip
                              title={refundNetwork}
                              placement="right"
                              arrow
                              componentsProps={{
                                tooltip: { sx: tooltipStyles.tooltip },
                                arrow: { sx: tooltipStyles.arrow },
                              }}
                            >
                              <div className="flex flex-col">
                                {!refundNetwork ? (
                                  "-"
                                ) : (
                                  <span className="text-[14px] font-semibold text-text ">
                                    {getNetworkDisplayName(refundNetwork)}
                                  </span>
                                )}
                              </div>
                            </Tooltip>
                          );
                        }
                        return (
                          <div className="flex flex-col">
                            {!refundNetwork ? (
                              "-"
                            ) : (
                              <span className="text-[14px] font-semibold text-text">
                                {getNetworkDisplayName(refundNetwork)}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </span>
                  </div>
                  <div className="flex">
                    <span className="w-[175px] text-sm text-[#64748B]">
                      Refund Address
                    </span>
                    <span className="text-sm font-semibold text-[#1E293B]">
                      {isMobile ? (
                        <>
                          {(() => {
                            if (walletAddress && walletAddress?.length > 10) {
                              return (
                                <Tooltip
                                  title={walletAddress}
                                  placement="right"
                                  arrow
                                  componentsProps={{
                                    tooltip: { sx: tooltipStyles.tooltip },
                                    arrow: { sx: tooltipStyles.arrow },
                                  }}
                                >
                                  <div className="flex flex-col">
                                    {!walletAddress ? (
                                      "-"
                                    ) : (
                                      <span className="flex items-center gap-3 text-[14px] font-semibold text-text">
                                        {truncateAddress(walletAddress, 4, 5)}{" "}
                                        <img
                                          src={"/images/copy.png"}
                                          alt="copy"
                                          className="cursor-pointer"
                                          onClick={handleCopy(walletAddress)}
                                        />
                                      </span>
                                    )}
                                  </div>
                                </Tooltip>
                              );
                            }
                            return (
                              <div className="flex flex-col">
                                {!walletAddress ? (
                                  "-"
                                ) : (
                                  <span className="flex items-center gap-3 text-[14px] font-semibold text-text">
                                    {walletAddress}{" "}
                                    <img
                                      src={"/images/copy.png"}
                                      alt="copy"
                                      className="cursor-pointer"
                                      onClick={handleCopy(walletAddress)}
                                    />
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </>
                      ) : (
                        <>
                          {(() => {
                            if (walletAddress && walletAddress?.length > 44) {
                              return (
                                <Tooltip
                                  title={walletAddress}
                                  placement="right"
                                  arrow
                                  componentsProps={{
                                    tooltip: { sx: tooltipStyles.tooltip },
                                    arrow: { sx: tooltipStyles.arrow },
                                  }}
                                >
                                  <div className="flex flex-col">
                                    {!walletAddress ? (
                                      "-"
                                    ) : (
                                      <span className="flex items-center gap-3 text-[14px] font-semibold text-text">
                                        {truncateAddress(walletAddress)}{" "}
                                        <img
                                          src={"/images/copy.png"}
                                          alt="copy"
                                          className="cursor-pointer"
                                          onClick={handleCopy(walletAddress)}
                                        />
                                      </span>
                                    )}
                                  </div>
                                </Tooltip>
                              );
                            }
                            return (
                              <div className="flex flex-col">
                                {!walletAddress ? (
                                  "-"
                                ) : (
                                  <span className="flex items-center gap-3 text-[14px] font-semibold text-text">
                                    {walletAddress}{" "}
                                    <img
                                      src={"/images/copy.png"}
                                      alt="copy"
                                      className="cursor-pointer"
                                      onClick={handleCopy(walletAddress)}
                                    />
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </>
                      )}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex">
                    <span className="w-[175px] text-sm text-[#64748B]">
                      Bank Name
                    </span>
                    <span className="text-sm font-semibold text-[#1E293B]">
                      {bankName}
                    </span>
                  </div>
                  <div className="flex">
                    <span className="w-[175px] text-sm text-[#64748B]">
                      Bank Account No.
                    </span>
                    <span className="text-sm font-semibold text-[#1E293B]">
                      {bankAccountNumber}
                    </span>
                  </div>
                  <div className="flex">
                    <span className="w-[175px] text-sm text-[#64748B]">
                      Name of Account
                    </span>
                    <span className="text-sm font-semibold text-[#1E293B]">
                      {bankAccountName}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
          {status === FlightRefundStatus.PENDING_ADMIN && (
            <div className="mt-8 flex w-full items-center gap-4 max-sm:h-full max-sm:flex-col max-sm:gap-2">
              <button
                onClick={handleReject}
                className="h-10 w-1/2 rounded-[6px]  bg-[#F75C5C] text-base font-medium text-white max-sm:block max-sm:h-12 max-sm:w-full max-sm:rounded-lg"
              >
                Reject
              </button>
              <button
                onClick={handleConfirm}
                className="h-10 w-1/2 rounded-[6px] bg-text text-base font-medium text-white max-sm:block max-sm:h-12 max-sm:w-full max-sm:rounded-lg"
              >
                Approve
              </button>
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default ReviewRefundFlightRequestModal;
